import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Box } from "@mantine/core";
import { useDisclosure, useElementSize } from "@mantine/hooks";
import { useUpdateSegment } from "../api/admin";
import { usePublicSettings } from "../api/queries";
import { usePlayer } from "../state/player";
import { useZone } from "../time/useZone";
import { useLayout } from "../ui/useLayout";
import { liveChip } from "./live";
import type { SnapKind } from "./snap";
import { useTimelineFeed } from "./Timeline/data";
import { type Cursor, useTimelineGestures } from "./Timeline/gestures";
import { useLanding } from "./Timeline/landing";
import { useCanvasSize, useTimelinePaint } from "./Timeline/paint";
import {
  type TimelineCtx,
  applySpan,
  applyZoom,
  setLiveDelay,
  useTimelineRefs,
  useTimelineWalls,
} from "./Timeline/state";
import { TimelineToolbar } from "./Timeline/toolbar";
import { BlockTooltip, type Tooltip } from "./Timeline/tooltip";

export function Timeline({ editable = false }: { editable?: boolean } = {}) {
  const zone = useZone();
  // Whether the bottom lane exists at all. Assumed absent until the settings resolve, which is
  // both the server's default and the same fallback the chat panel and the push stream use.
  // Guessing the other way would cost every deployment a visible 24 px collapse a frame after
  // load; guessing this way costs the ones that do log chat a single grow, once.
  const settings = usePublicSettings().data;
  const chatLane = settings?.chat_enabled ?? false;
  const layout = useLayout();
  // `short`, and not `phone`: the canvas is compact whichever way round the window is, and a
  // tall narrow window must keep its full height.
  const compact = layout.short;
  // Zero until the settings resolve, which is the answer that claims nothing: no band is drawn
  // and the wall stays where it was before this was served. Guessing a duration instead would
  // put a wall somewhere the server never asked for, and for the one round trip it saves.
  const liveDelayMs = (settings?.live_delay_seconds ?? 0) * 1000;
  const r = useTimelineRefs();
  const queryClient = useQueryClient();
  // Only the stable mutate functions, not the mutation object: its identity changes on
  // every render, and anything downstream that depends on it (commitEdge, the pointer
  // listener effect) would be torn down mid-drag by the first setState of the drag.
  const { mutate: mutateSegment, mutateAsync: mutateSegmentAsync } = useUpdateSegment();
  const mutations = useMemo(
    () => ({ mutateSegment, mutateSegmentAsync }),
    [mutateSegment, mutateSegmentAsync],
  );
  const { t } = useTranslation();

  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [cursor, setCursor] = useState<Cursor>("grab");
  const [snapNote, setSnapNote] = useState<SnapKind | null>(null);
  // Deliberately not `positionMs`: this component must not re-render on every position update,
  // and none of these change while audio simply plays.
  const atLive = usePlayer((s) => s.atLive);
  const playing = usePlayer((s) => s.playing);
  const source = usePlayer((s) => s.source);

  // Mirrored out of React for the same reason the window is: the render loop, the pan stop and
  // the click tests all read it outside a render, and a dependency on it would tear the
  // animation loop down and rebuild it the moment the settings query resolved.
  useEffect(() => setLiveDelay(r, liveDelayMs), [r, liveDelayMs]);

  const walls = useTimelineWalls(r);
  const { data, syncWindow, latestMs, markDirty } = useTimelineFeed(r, editable);
  const ctx = useMemo<TimelineCtx>(
    () => ({ refs: r, walls, latestMs, syncWindow, editable, t }),
    [r, walls, latestMs, syncWindow, editable, t],
  );
  const landing = useLanding(useMemo(() => ({ ...ctx, queryClient }), [ctx, queryClient]));

  useCanvasSize(r, chatLane, compact, syncWindow);
  useTimelinePaint(
    useMemo(
      () => ({ ...ctx, zone: zone.name, chatLane, compact }),
      [ctx, zone.name, chatLane, compact],
    ),
  );
  useTimelineGestures(
    useMemo(
      () => ({ ...ctx, landing, mutations, setCursor, setTooltip, setSnapNote }),
      [ctx, landing, mutations],
    ),
  );

  // Redraw when something outside the loop's own reach changes.
  useEffect(() => {
    const unsubscribe = usePlayer.subscribe(markDirty);
    return unsubscribe;
  }, [markDirty]);
  useEffect(markDirty, [zone.name, markDirty]);

  const setSpan = useCallback(
    (spanMs: number) => applySpan(r, walls, syncWindow, spanMs),
    [r, walls, syncWindow],
  );
  const zoomBy = useCallback(
    (factor: number) => applyZoom(r, walls, syncWindow, factor),
    [r, walls, syncWindow],
  );

  const [zoomsOpen, { toggle: toggleZooms }] = useDisclosure(false);
  // The presets keep `width: max-content` inside a box that closes to nothing, so this stays
  // their real laid-out width at every window size -- including the phone, where the nudge
  // pair is `display: none` and the row is genuinely narrower.
  const { ref: presetsRef, width: presetsWidth } = useElementSize();

  const live = liveChip({ atLive, sourceKind: source?.kind ?? null, playing });

  return (
    <Box component="section" aria-label={t("timeline.ariaLabel")}>
      <TimelineToolbar
        t={t}
        phone={layout.phone}
        coarse={layout.coarse}
        zoomsOpen={zoomsOpen}
        toggleZooms={toggleZooms}
        presetsRef={presetsRef}
        presetsWidth={presetsWidth}
        setSpan={setSpan}
        zoomBy={zoomBy}
        editable={editable}
        snapNote={snapNote}
        live={live}
        enterLive={landing.enterLive}
        chatLane={chatLane}
        listened={(data?.listened ?? null) !== null}
      />
      <Box ref={r.container} pos="relative" w="100%">
        {/* `touch-action` has no Mantine prop and no theme home: the wheel handler zooms, and
            without it the browser also scrolls the page. `max-width` is the one guard on the
            inline pixel width the resize effect writes here -- a window resized while the tab
            is occluded leaves the canvas wide, and unclamped that widens the document until
            something repaints. */}
        <canvas
          ref={r.canvas}
          style={{
            cursor,
            display: "block",
            maxWidth: "100%",
            touchAction: "none",
            WebkitTapHighlightColor: "transparent",
          }}
        />
        {tooltip && <BlockTooltip tooltip={tooltip} zoneName={zone.name} />}
      </Box>
    </Box>
  );
}
